function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import moment from "moment";
import { Container, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import Chart from "../../components/chart";
import Markdown from "../market-intelligence/markdown";
import { forecastLens } from "../../functions/tenant-features";
import { statusInfo, isTerminal, isLegacyRun, pillTone } from "./similarity";
import { Link } from "react-router-dom";

/*
 * Similarity Forecast — redesigned results (FORECASTING_PROGRAM.md 2.1-2.3;
 * mockup similarity-forecast-redesign.html). One results component, two
 * framings: the tenant's ForecastLens decides whether the run reads as a
 * manufacturer demand forecast ("who do we sell it to") or a retail buy
 * decision ("where does it fit in my stores").
 *
 * Explainability rules coded here (spec 2.3): the 5 comparables are ALWAYS
 * visible with images + scores; the confidence chip derives from the top
 * comparable score (<0.5 → "Low — treat as directional"); data freshness =
 * lastUpdated; contacts 0 renders the friendly line, never a bare 0.
 *
 * The SimilarityResults component is deliberately self-contained so the
 * Studio wizard's forecast step can render the same thing later.
 */

const fmtNum = v => (Number(v) || 0).toLocaleString();
function svc(api) {
  const Service = ServiceFactory.getType(api, "forecast");
  return new Service();
}
function confidence(neighbors) {
  const top = Math.max(0, ...(neighbors || []).map(n => Number(n.similarityScore) || 0));
  if (!neighbors || !neighbors.length) return null;
  if (top < 0.5) return {
    label: "Low confidence — treat as directional",
    cls: "warn"
  };
  if (top < 0.72) return {
    label: "Moderate confidence",
    cls: "info"
  };
  return {
    label: "Good confidence",
    cls: "ok"
  };
}

// sales-prediction rows: storeId === "" is the chain-wide series; named
// storeIds are per-store slices. 12 monthly rows each.
function splitPrediction(rows) {
  const chain = [];
  const byStore = {};
  (rows || []).forEach(r => {
    const q = Number(r.quantity) || 0;
    const lo = r.quantityLower != null ? Number(r.quantityLower) : null;
    const hi = r.quantityUpper != null ? Number(r.quantityUpper) : null;
    if (!r.storeId) chain.push({
      date: r.date,
      q,
      lo,
      hi
    });else {
      byStore[r.storeId] = byStore[r.storeId] || [];
      byStore[r.storeId].push({
        date: r.date,
        q
      });
    }
  });
  const stores = Object.keys(byStore).map(name => ({
    name,
    units: byStore[name].reduce((a, r) => a + r.q, 0)
  })).sort((a, b) => b.units - a.units);
  // 95% band (quantityLower/Upper — shipped 2026-08-11; null on runs
  // predating it until refreshed). Band totals only when every row has one.
  const hasBand = chain.length > 0 && chain.every(r => r.lo != null && r.hi != null);
  return {
    chain,
    stores,
    chainUnits: chain.reduce((a, r) => a + r.q, 0),
    chainLower: hasBand ? chain.reduce((a, r) => a + r.lo, 0) : null,
    chainUpper: hasBand ? chain.reduce((a, r) => a + r.hi, 0) : null
  };
}
function fitLabel(units, max) {
  if (max <= 0) return "—";
  const ratio = units / max;
  if (ratio >= 0.75) return "strong";
  if (ratio >= 0.4) return "moderate";
  return "weak";
}
const REC_TITLES = {
  score: "Performance outlook",
  gtm: "Go-to-market",
  location: "Launch markets",
  marketing: "Audience & channel"
};
const REC_ORDER = ["score", "gtm", "location", "marketing"];
export function SimilarityResults({
  lens,
  run,
  detail,
  interest,
  prediction,
  neighbors,
  places,
  contacts,
  contactCount,
  recommendations
}) {
  const retail = lens === "retail";
  const {
    stores,
    chainUnits,
    chain,
    chainLower,
    chainUpper
  } = splitPrediction(prediction);
  const conf = confidence(neighbors);
  const topNeighbor = (neighbors || [])[0];
  const totalViews = run ? Number(run.views) || 0 : (interest || []).reduce((a, r) => a + (Number(r.views) || 0), 0);
  const totalUsers = run ? Number(run.uniqueUsers) || 0 : (interest || []).reduce((a, r) => a + (Number(r.uniqueUsers) || 0), 0);
  const nContacts = contactCount != null ? contactCount : run ? Number(run.contacts) || 0 : 0;

  // Monthly chart: units (bars) + views (line), merged by month.
  const byMonth = {};
  chain.forEach(r => {
    byMonth[String(r.date).slice(0, 7)] = {
      units: r.q,
      lo: r.lo,
      hi: r.hi
    };
  });
  (interest || []).forEach(r => {
    const k = String(r.date).slice(0, 7);
    byMonth[k] = byMonth[k] || {};
    byMonth[k].views = Number(r.views) || 0;
  });
  const months = Object.keys(byMonth).sort();
  const withBand = chainLower != null && chainUpper != null;
  const chartData = months.length >= 2 ? withBand ? [["Month", "Predicted units", {
    role: "interval"
  }, {
    role: "interval"
  }, "Predicted views"]].concat(months.map(m => [moment(m + "-01").format("MMM YY"), Math.round((byMonth[m].units || 0) * 100) / 100, byMonth[m].lo != null ? Math.round(byMonth[m].lo * 100) / 100 : null, byMonth[m].hi != null ? Math.round(byMonth[m].hi * 100) / 100 : null, byMonth[m].views != null ? byMonth[m].views : null])) : [["Month", "Predicted units", "Predicted views"]].concat(months.map(m => [moment(m + "-01").format("MMM YY"), Math.round((byMonth[m].units || 0) * 100) / 100, byMonth[m].views != null ? byMonth[m].views : null])) : null;
  if (chartData) chartData.withBand = withBand;
  const recs = REC_ORDER.map(cat => (recommendations || []).find(r => r.category === cat)).filter(Boolean);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Ribbon, {
    comparing: null,
    tiles: [{
      l: "Predicted units · 12 months",
      v: chainUnits > 0 ? Math.round(chainUnits).toLocaleString() : "—",
      s: [chainLower != null && chainUpper != null && chainUnits > 0 ? `range ${Math.round(chainLower).toLocaleString()} to ${Math.round(chainUpper).toLocaleString()}` : null, conf ? conf.label : null, retail && chainUnits > 0 ? "chain-wide, from comparable sales" : null].filter(Boolean).join(" · ") || null
    }, {
      l: "Predicted interest · 12 months",
      v: fmtNum(totalViews),
      s: `${fmtNum(totalUsers)} unique shoppers`
    }, {
      l: retail ? "Where it fits" : "Top markets",
      v: retail ? stores.length ? stores[0].name : "—" : (places || []).length ? places[0].name : "—",
      s: retail ? stores.length ? stores.slice(0, 3).map(s => `${s.name} (${fitLabel(s.units, stores[0].units)})`).join(" · ") : "scoped to this forecast" : (places || []).length ? places.slice(0, 4).map(p => p.name).join(" · ") : "scoped to this forecast"
    }, {
      l: "Interested contacts",
      v: nContacts > 0 ? fmtNum(nContacts) : "0",
      s: nContacts === 0 ? "interest builds as the comparables get traffic" : "engaged with the comparables"
    }, retail && topNeighbor ? {
      l: "Assortment",
      v: Number(topNeighbor.similarityScore) >= 0.95 ? "Matches" : "New",
      s: `closest: ${topNeighbor.title} (${(Number(topNeighbor.similarityScore) || 0).toFixed(2)})`
    } : null].filter(Boolean)
  }), /*#__PURE__*/React.createElement("div", {
    className: "wa-grid"
  }, /*#__PURE__*/React.createElement(Card, {
    title: retail ? "Competes with" : "Built from these comparables",
    hint: retail ? "the comparables behind this forecast: they are also the products a buy would compete with" : "this forecast projects from how these products actually performed · closer scores mean stronger evidence"
  }, /*#__PURE__*/React.createElement("div", {
    className: "simf-thumbrow"
  }, (neighbors || []).map(n => /*#__PURE__*/React.createElement("a", {
    key: n.id,
    className: "simf-thumb",
    href: n.link,
    target: "_blank",
    rel: "noopener noreferrer",
    title: n.title
  }, /*#__PURE__*/React.createElement("span", {
    className: "tag"
  }, (Number(n.similarityScore) || 0).toFixed(2)), /*#__PURE__*/React.createElement("img", {
    src: n.imageLink,
    alt: n.title,
    onError: ({
      currentTarget
    }) => {
      currentTarget.onerror = null;
      currentTarget.src = "https://s3.amazonaws.com/app.plunk.io/images/image-404.png";
    }
  }), /*#__PURE__*/React.createElement("em", null, n.title))), !(neighbors || []).length && /*#__PURE__*/React.createElement(Empty, null, "Comparables unavailable for this run."))), chartData && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    title: "Monthly forecast",
    hint: "predicted units as bars \xB7 predicted views as a line",
    span: retail && stores.length > 0 ? 8 : 12
  }, /*#__PURE__*/React.createElement(Chart, {
    width: "100%",
    height: "300px",
    chartType: "ComboChart",
    data: chartData,
    options: {
      seriesType: "bars",
      series: {
        0: {
          targetAxisIndex: 0,
          color: "#ff6358",
          tooltipFormat: "{0:n1}"
        },
        1: {
          type: "line",
          targetAxisIndex: 1,
          color: "#78d237",
          tooltipFormat: "{0:n0}"
        }
      },
      vAxes: {
        0: {
          title: "Units"
        },
        1: {
          title: "Views"
        }
      },
      legend: {
        position: "top"
      }
    }
  }))), retail && stores.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    title: "Fit by store",
    hint: "how strongly products like this sell in each store, weighted by visual similarity \xB7 ranked against the strongest store",
    span: 4
  }, /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Store"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Predicted units (12 mo)"), /*#__PURE__*/React.createElement("th", null, "Fit"))), /*#__PURE__*/React.createElement("tbody", null, stores.map(s => /*#__PURE__*/React.createElement("tr", {
    key: s.name
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
    className: "name"
  }, s.name)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, s.units >= 1 ? Math.round(s.units).toLocaleString() : s.units.toFixed(1)), /*#__PURE__*/React.createElement("td", {
    style: {
      textTransform: "capitalize"
    }
  }, fitLabel(s.units, stores[0].units))))))))), recs.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    title: "Recommendations",
    hint: "from the comparables and this market's demand"
  }, /*#__PURE__*/React.createElement("div", {
    className: "simf-recs"
  }, recs.map(r => /*#__PURE__*/React.createElement("div", {
    key: r.category,
    className: "simf-rec"
  }, /*#__PURE__*/React.createElement("h4", null, REC_TITLES[r.category] || r.category), /*#__PURE__*/React.createElement(Markdown, {
    source: r.answer,
    className: "simf-rec-body"
  })))))), (contacts || []).length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    title: "Interested contacts",
    hint: `${fmtNum(nContacts)} total · contacts who engaged with the comparables`
  }, /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Name"), /*#__PURE__*/React.createElement("th", null, "Email"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Sessions"), /*#__PURE__*/React.createElement("th", null, "Last seen"))), /*#__PURE__*/React.createElement("tbody", null, contacts.map((c, i) => /*#__PURE__*/React.createElement("tr", {
    key: i
  }, /*#__PURE__*/React.createElement("td", {
    style: {
      textTransform: "capitalize"
    }
  }, c.name || "—"), /*#__PURE__*/React.createElement("td", null, c.email || "—"), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmtNum(c.sessions)), /*#__PURE__*/React.createElement("td", null, c.lastSeen ? String(c.lastSeen).slice(0, 10) : "—"))))))))));
}
class MainComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onRefresh", () => {
      svc(`/forecast/product-similarity-forecast/${this.id}/refresh`).get({}, err => {
        if (this.toast) this.toast.show(err ? "Error queuing the re-run." : "Re-run queued — check back in a few minutes.");
      });
    });
    _defineProperty(this, "onDelete", () => {
      svc("/forecast/product-similarity-forecast").delete(String(this.id), err => {
        if (this.toast) this.toast.show(err ? "Error deleting the forecast." : "Forecast deleted.");
        if (!err) this.props.history.push("/Forecast/Similarity");
      });
    });
    this.state = {
      loaded: false,
      error: null,
      confirmDelete: false
    };
    this._alive = true;
  }
  componentDidMount() {
    publishSearch("Similarity Forecast");
    publishEvent("reset-search", {});
    this.toast = document.querySelector("paper-toast");
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  get id() {
    return this.props.params.id;
  }
  async load() {
    const id = this.id;
    const base = "/forecast/product-similarity-forecast";
    try {
      const [detail, list, interest, prediction, neighbors, places, contacts, recs] = await Promise.all([svc(`${base}/${id}/detail`).getAsync(), svc(`${base}?$top=50&$orderByDesc=CreatedDate`).getAsync().catch(() => null), svc(`${base}/${id}?$top=24&$orderBy=Date`).getAsync().catch(() => null), svc(`${base}/sales-prediction/${id}`).getAsync().catch(() => null), svc(`${base}/product/${id}/title?$orderBy=Title&$top=10`).getAsync().catch(() => null), svc(`${base}/place/${id}/name?$orderBy=Name&$top=20`).getAsync().catch(() => null), svc(`${base}/contact/${id}?$orderByDesc=Sessions&$top=10`).getAsync().catch(() => null), svc(`${base}/recommendation/${id}`).getAsync().catch(() => null)]);
      if (!this._alive) return;
      const run = list && (list.items || []).find(r => String(r.id) === String(id));
      const sortedNeighbors = (neighbors && neighbors.items || []).slice().sort((a, b) => (Number(b.similarityScore) || 0) - (Number(a.similarityScore) || 0));
      this.setState({
        loaded: true,
        detail,
        run,
        interest: interest && interest.items || [],
        prediction: prediction && prediction.items || [],
        neighbors: sortedNeighbors,
        places: places && places.items || [],
        contacts: contacts && contacts.items || [],
        contactCount: contacts ? contacts.count : null,
        recommendations: recs && recs.items || []
      });
    } catch (err) {
      if (this._alive) this.setState({
        loaded: true,
        error: err.message || "Could not load this forecast."
      });
    }
  }
  render() {
    const {
      loaded,
      error,
      detail,
      run,
      confirmDelete
    } = this.state;
    if (!loaded) return /*#__PURE__*/React.createElement(Loader, null);
    if (error) return /*#__PURE__*/React.createElement(Error, {
      message: error
    });
    const lens = forecastLens();
    const running = run && !isTerminal(run.status);
    const st = run ? statusInfo(run.status) : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view simf-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Forecast/Similarity"
      }, "Forecasts"), " \xB7 Forecast"),
      title: detail.name,
      subtitle: [detail.category || null, `comparables refreshed ${moment(detail.lastUpdated).format("MMMM D, YYYY")}`].filter(Boolean).join(" · "),
      chips: [st ? {
        k: "Status",
        v: st.label
      } : null, run && isLegacyRun(run) ? {
        k: "Engine",
        v: "legacy · numbers predate the current matcher"
      } : null],
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: this.onRefresh
      }, "Re-run"), confirmDelete ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-error",
        onClick: this.onDelete
      }, "Confirm delete"), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.setState({
          confirmDelete: false
        })
      }, "Cancel")) : /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.setState({
          confirmDelete: true
        })
      }, "Remove"))
    }), /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement("div", {
      className: "simf-head"
    }, /*#__PURE__*/React.createElement("img", {
      className: "simf-head-img",
      src: detail.imageLink,
      alt: detail.name,
      onError: ({
        currentTarget
      }) => {
        currentTarget.onerror = null;
        currentTarget.src = "https://s3.amazonaws.com/app.plunk.io/images/image-404.png";
      }
    }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
      style: {
        margin: "0 0 2px"
      }
    }, detail.name), detail.category ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 4px"
      }
    }, detail.category) : null, detail.description ? /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 6px",
        maxWidth: "70ch"
      }
    }, detail.description) : null, st ? /*#__PURE__*/React.createElement(Pill, {
      tone: pillTone(st.cls)
    }, st.label) : null))), running ? /*#__PURE__*/React.createElement(Empty, null, "Still running. Steps: analyzing image, finding comparables, forecasting, recommendations. Usually 2 to 5 minutes; refresh this page in a moment.") : /*#__PURE__*/React.createElement(SimilarityResults, {
      lens: lens,
      run: run,
      detail: detail,
      interest: this.state.interest,
      prediction: this.state.prediction,
      neighbors: this.state.neighbors,
      places: this.state.places,
      contacts: this.state.contacts,
      contactCount: this.state.contactCount,
      recommendations: this.state.recommendations
    }));
  }
}
const Routable = withRouter(MainComponent);
export class ForecastSimilarityDetailViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}
export default {
  ForecastSimilarityDetailViewComponent
};